import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListTable, Button, Modal, Textinput, SubPage } from '../../../ui';
import Group from './Group';
// import { selectCreateStatus, selectNewlyCreated } from '../selectors'; // Commented out - using RTK Query instead
// import { setNewlyCreated, clearNewlyCreated } from '../groupsSlice'; // Commented out - using RTK Query instead
import { useEditableGroups } from '../hooks/useGroupsState';
import { useGroupsActions } from '../hooks/useGroupsActions';
import Loading from '../../../ui/Loading';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../ui/Page/PageHeader';
import PageControls from '../../../ui/Page/PageControls';

const GroupsList = ({ modal }) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortMode, setSortMode] = useState('asc');
  const [isCreateGroup, setIsCreateGroup] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);
  const groupNameRef = useRef();

  const navigate = useNavigate();

  const { groups, isLoading } = useEditableGroups({
    disableCounts: false
  });

  const { deleteGroup, createGroup } = useGroupsActions();

  const openGroup = useCallback((obj) => {
    navigate(`/users/groups/${obj.id}`)
  }, [navigate]);

  // FIX: Use useMemo instead of useEffect to avoid infinite loop from groups array changing
  const filteredGroups = useMemo(() => {
    return groups.filter(group => group.name.toLowerCase().includes(searchQuery))
  }, [groups, searchQuery]);

  const handleSearch = useCallback((value) => {
    setSearchQuery(value)
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const handleSort = useCallback((val) => {
    setSortBy(val);
    if (sortBy === val) {
      setSortMode('desc')
    }
    if (sortMode === 'desc') {
      setSortMode('asc')
    }
  }, [setSortBy, sortBy, setSortMode, sortMode]);

  const deleteGroupConfirmed = async () => {
    await deleteGroup(groupToDelete.id);
    setGroupToDelete(null);
  }

  const tableInfo = [
    { label: t('globals.name'), handleClick: () => handleSort('name'), data: (item) => item.name },
    { label: t('globals.created'), data: (item) => item.created },
    { label: t('globals.courses'), data: (item) => item.courseCount },
    { label: t('globals.users'), data: (item) => item.studentCount },
    {
      label: '', menuOptions: () => [
        { label: t('groups.groupDetails'), onClick: openGroup },
        { label: t('groups.deleteGroup'), onClick: (item) => setGroupToDelete(item) },
      ]
    },
  ]

  const createGroupHandler = () => {
    setIsCreateGroup(true)
  }

  const saveGroup = async () => {
    const result = await createGroup(groupNameRef.current.value);
    setIsCreateGroup(false);
    openGroup(result);
  }

  return (
    <SubPage staticElements={
      <PageHeader>
        <PageControls
          onClearSearch={clearSearch}
          onSearch={handleSearch}
          placeholder={t('groups.searchPlaceholder')}
          disableClear={!searchQuery}
          openModelButton={
            <Button icon='Edit' onClick={createGroupHandler}>{t('groups.createGroup')}</Button>
          }
        />
      </PageHeader>
    }>
      {isLoading ? <Loading /> :
        <ListTable
          tableInfo={tableInfo}
          data={filteredGroups}
          onRowClick={openGroup}
          tableName={'groups'}
          keepTrackOfPage={setSavedCurrentPage}
          defaultStartPage={savedCurrentPage}
        />}
      <Group open={!!modal} />
      <ConfirmDelete
        onClose={setGroupToDelete}
        onAcceptDelete={deleteGroupConfirmed}
        open={!!groupToDelete?.id}
        description={t('groups.confirmDelete', { groupToDelete: groupToDelete?.name })}
      />
      <Modal open={isCreateGroup}
        onClose={() => setIsCreateGroup(false)}
        showActions
        onAccept={saveGroup} title={t('groups.createGroup')}
      >
        <Textinput
          placeholder={t('course.courseName')}
          innerRef={groupNameRef}
        />
      </Modal>
    </SubPage >
  );
};

export default GroupsList;
